import type { RouteObject } from "react-router-dom";
import NotFound from "../pages/NotFound";
import Home from "../pages/home/page";
import LoginPage from "../pages/login/page";
import ModuleDetailPage from "../pages/module-detail/page";
import ForecastPage from "../pages/forecast/page";
import ForecastRedesignedPage from "../pages/forecast/redesign/page";
import ForecastMmsPage from "../pages/dispatch/forecast-mms/page";
import ScheduleDraftMockPage from "../pages/dispatch/schedule-draft/page";
import EmsPage from "../pages/ems/page";
import SaleTermsPage from "../pages/financial/sale-terms/page";
import FinancialResultPage from "../pages/financial/result/page";
import BessPage from "../pages/bess/page";
import SetupWizardPage from "../pages/setup-wizard/page";
import OnboardingPage from "../pages/onboarding/page";
import AboutPage from "../pages/about/page";
import DocumentsHubPage from "../pages/docs/page";
import ContactsPage from "../pages/contacts/page";
import CounterpartiesPage from "../pages/counterparties/page";
import CounterpartyDetailPage from "../pages/counterparties/detail/page";
import AssetsPage from "../pages/assets/page";
import AssetDetailPage from "../pages/assets/detail/page";
import AssetCreatePage from "../pages/assets/create/page";
import ContractsPage from "../pages/contracts/page";
import DocumentsPage from "../pages/documents/page";
import SettingsPage from "../pages/settings/page";
import TenantsPage from "../pages/admin/tenants/page";
import TenantDetailPage from "../pages/admin/tenants/detail/page";
import TenantUsersPage from "../pages/admin/users/page";
import RolesPage from "../pages/admin/roles/page";
import PlatformDashboardPage from "../pages/platform/dashboard/page";
import PlatformTenantsPage from "../pages/platform/tenants/page";
import PlatformUsersPage from "../pages/platform/users/page";
import PlatformCounterpartiesPage from "../pages/platform/counterparties/page";
import PlatformAssetsPage from "../pages/platform/assets/page";

import UeexPage from '@/pages/market/ueex/page';
import MarketPricesPage from '@/pages/market/prices/page';
import { Navigate } from "react-router-dom";
import BudgetPage from '@/pages/budget/page';
import SkoPage from '@/pages/sko/page';
import OsrPage from '@/pages/osr/page';
import DeeplinkPage from '@/pages/auth/DeeplinkPage';
import OwnerDashboardPage from '@/pages/dashboard/owner/page';
import OwnerDashboardLab from '@/pages/dashboard/owner/lab';
import ComDispPage from '@/pages/dashboard/owner/comdisp/page';
import DispatcherDashboardPage from '@/pages/dashboard/dispatcher/page';
import MathDashPage from '@/pages/mathdash/page';
import TradingOverviewPage from '@/pages/trading/page';
import TradingDamPage from '@/pages/trading/dam/page';
import TradingIdmPage from '@/pages/trading/idm/page';
import InfoAnalyticsPage from '@/pages/info/page';

// SCADA dispatcher module (prototype, mock-fed)
import ScadaDispatchPage from '@/pages/scada/dispatch/page';
import ScadaControlPage from '@/pages/scada/control/page';
import ScadaStationPage from '@/pages/scada/station/page';
import ScadaSkoPage from '@/pages/scada/sko/page';
import ScadaEventsPage from '@/pages/scada/events/page';
import ScadaShiftHandoverPage from '@/pages/scada/shift-handover/page';
import ScadaEmsBoxPage from '@/pages/scada/emsbox/page';
import ScadaReportsPage from '@/pages/scada/reports/page';
import ScadaLayout from '@/components/feature/ScadaLayout';

const routes: RouteObject[] = [
  // ВЕСЬ розділ SCADA — під ОДНИМ батьківським layout. Раніше кожна сторінка
  // сама рендерила <ScadaLayout>, тож при кожному переході каркас (ідентифікація
  // станції, підменю, шар теми) розмонтовувався й будувався наново, а блок
  // «ТОВ ТЕПЛА / EIC» узагалі існував лише там, де сторінка передала його пропом.
  // Тепер каркас — спільний батько, а сторінки — його <Outlet />.
  {
    path: "/scada",
    element: <ScadaLayout />,
    children: [
      { path: "dispatch", element: <ScadaDispatchPage /> },
      { path: "control", element: <ScadaControlPage /> },
      // Вкладка картки станції — частина адреси. Старий шлях без :tab лишається
      // зареєстрованим, тож наявні посилання (перелік СЕС, закладки) не ламаються
      // і відкривають «Огляд» — редиректи не потрібні.
      { path: "station/:id", element: <ScadaStationPage /> },
      { path: "station/:id/:tab", element: <ScadaStationPage /> },
      { path: "sko", element: <ScadaSkoPage /> },
      { path: "events", element: <ScadaEventsPage /> },
      { path: "shift-handover", element: <ScadaShiftHandoverPage /> },
      { path: "emsbox", element: <ScadaEmsBoxPage /> },
      { path: "reports", element: <ScadaReportsPage /> },
    ],
  },
  {
    path: "/sko",
    element: <SkoPage />,
  },
  {
    path: "/osr",
    element: <OsrPage />,
  },
  {
    path: "/trading",
    element: <TradingOverviewPage />,
  },
  {
    path: "/trading/dam",
    element: <TradingDamPage />,
  },
  {
    path: "/trading/idm",
    element: <TradingIdmPage />,
  },
  {
    path: "/market/ueex",
    element: <UeexPage />,
  },
  {
    path: "/market/prices",
    element: <MarketPricesPage />,
  },
  {
    path: "/market/price-forecast",
    element: <Navigate to="/market/prices" replace />,
  },
  {
    path: "/budget",
    element: <BudgetPage />,
  },
  {
    path: "/info",
    element: <InfoAnalyticsPage />,
  },

  {
    path: "/",
    element: <Home />,
  },
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    // Route-swap: на стенді (VITE_REDESIGN='1') — редизайн, у проді — стара сторінка.
    path: "/forecast",
    element: import.meta.env.VITE_REDESIGN === '1' ? <ForecastRedesignedPage /> : <ForecastPage />,
  },
  {
    path: "/dispatch/forecast-mms",
    element: <ForecastMmsPage />,
  },
  {
    // МАКЕТ торгового графіка (концепція 13.10): модель пропонує — троє погоджують.
    // Демонстраційні дані, жодного запиту до API; показуємо Вадиму, Олі й
    // технічному диспетчеру ДО того, як писати функціонал.
    path: "/dispatch/schedule-draft",
    element: <ScheduleDraftMockPage />,
  },
  {
    path: "/ems",
    element: <EmsPage />,
  },
  {
    path: "/financial/sale-terms",
    element: <SaleTermsPage />,
  },
  {
    path: "/financial/result",
    element: <FinancialResultPage />,
  },
  {
    path: "/bess",
    element: <BessPage />,
  },
  {
    path: "/setup-wizard",
    element: <SetupWizardPage />,
  },
  {
    path: "/onboarding",
    element: <OnboardingPage />,
  },
  {
    path: "/about",
    element: <AboutPage />,
  },
  {
    path: "/docs",
    element: <DocumentsHubPage />,
  },
  {
    path: "/contacts",
    element: <ContactsPage />,
  },
  {
    path: "/counterparties",
    element: <CounterpartiesPage />,
  },
  {
    path: "/counterparties/:id",
    element: <CounterpartyDetailPage />,
  },
  {
    path: "/assets",
    element: <AssetsPage />,
  },
  {
    path: "/assets/:id",
    element: <AssetDetailPage />,
  },
  {
    path: "/counterparties/:counterpartyId/assets/new",
    element: <AssetCreatePage />,
  },
  {
    path: "/contracts",
    element: <ContractsPage />,
  },
  {
    path: "/documents",
    element: <DocumentsPage />,
  },
  {
    path: "/settings",
    element: <SettingsPage />,
  },
  {
    path: "/admin/tenants",
    element: <TenantsPage />,
  },
  {
    path: "/admin/tenants/:id",
    element: <TenantDetailPage />,
  },
  {
    path: "/admin/users",
    element: <TenantUsersPage />,
  },
  {
    path: "/admin/roles",
    element: <RolesPage />,
  },
  {
    path: "/platform/dashboard",
    element: <PlatformDashboardPage />,
  },
  {
    path: "/platform/tenants",
    element: <PlatformTenantsPage />,
  },
  {
    path: "/platform/users",
    element: <PlatformUsersPage />,
  },
  {
    path: "/platform/counterparties",
    element: <PlatformCounterpartiesPage />,
  },
  {
    path: "/platform/assets",
    element: <PlatformAssetsPage />,
  },
  {
    path: "/module/:moduleId",
    element: <ModuleDetailPage />,
  },
  {
    path: "/auth/deeplink/:token",
    element: <DeeplinkPage />,
  },
  {
    path: "/mathdash",
    element: <MathDashPage />,
  },
  {
    path: "/dashboard/dispatcher",
    element: <DispatcherDashboardPage />,
  },
  {
    path: "/dashboard/owner",
    element: <OwnerDashboardPage />,
  },
  {
    path: "/dashboard/owner/comdisp",
    element: <ComDispPage />,
  },
  {
    path: "/dashboard/owner/lab",
    element: <OwnerDashboardLab />,
  },
  {
    path: "*",
    element: <NotFound />,
  },
];

export default routes;